import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogService, CatalogWarehouse, InventoryScanLookup, WarehouseTask } from './catalog.service';
import { bindInventoryLiveUpdates } from './inventory-live-updates';

@Component({
  standalone: true,
  selector: 'ain-inventory-operator',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.operator.title')" [description]="t('catalog.operator.description')">
      <ain-inventory-page-hint pageKey="operator" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">
        {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
      </span>
      @if (loading()) { <ain-state type="loading" [title]="t('catalog.loading')" /> }
      @else {
        <section class="card">
          <h3>{{ t('catalog.operator.scanTitle') }}</h3>
          <div class="row">
            <p-select [(ngModel)]="warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" appendTo="body" [placeholder]="t('catalog.inventory.warehouseId')" />
            <input pInputText [(ngModel)]="scanCode" (keyup.enter)="scan()" [placeholder]="t('catalog.operator.scanPlaceholder')" />
            <button pButton type="button" [label]="t('catalog.operator.scan')" (click)="scan()"></button>
          </div>
          @if (lookup()) {
            <p class="result">{{ lookup()!.entityType }}: {{ lookup()!.code }} — {{ lookup()!.detail }}</p>
            <div class="actions">
              @if (lookup()!.entityType === 'WarehouseTask') {
                <button pButton type="button" size="small" [label]="t('catalog.operator.openTask')" (click)="openTask(lookup()!.entityId)"></button>
              }
              @if (lookup()!.entityType === 'WarehouseBin') {
                <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.operator.filterBin')" (click)="filterTasksByBin(lookup()!.entityId)"></button>
              }
            </div>
          }
        </section>
        <section class="card">
          <h3>{{ t('catalog.outbound.tasksTitle') }}</h3>
          @for (task of filteredTasks(); track task.id) {
            <div class="task-row">
              <span>{{ task.taskType }} · {{ task.quantity }} · {{ task.status }}</span>
              <span>
                @if (task.status === 'Pending' && task.taskType === 'PutAway') {
                  <button pButton type="button" size="small" [label]="t('catalog.inbound.completePutAway')" (click)="completePutAway(task)"></button>
                }
                @if (task.status === 'Pending' && task.taskType === 'Pick') {
                  <button pButton type="button" size="small" [label]="t('catalog.outbound.completePick')" (click)="completePick(task)"></button>
                }
                @if (task.status === 'Pending' && task.taskType === 'Pack') {
                  <button pButton type="button" size="small" [label]="t('catalog.operator.completePack')" (click)="completePack(task)"></button>
                }
                @if (task.status === 'Pending' && task.taskType === 'Ship') {
                  <button pButton type="button" size="small" [label]="t('catalog.operator.completeShip')" (click)="completeShip(task)"></button>
                }
                @if (task.status === 'Completed' && task.taskType === 'Pack') {
                  <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.operator.createShip')" (click)="createShip(task)"></button>
                }
              </span>
            </div>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; }
    .row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .row input { flex: 1; min-width: 200px; }
    .result { margin-top: 8px; font-weight: 600; color: #334155; }
    .actions { margin-top: 8px; display: flex; gap: 8px; }
    .task-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f1f5f9; gap: 8px; flex-wrap: wrap; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryOperatorComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly tasks = signal<WarehouseTask[]>([]);
  readonly filteredTasks = signal<WarehouseTask[]>([]);
  readonly lookup = signal<InventoryScanLookup | null>(null);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  warehouseId = '';
  scanCode = '';
  t = (k: string) => this.localization.translate(k);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload()
    );
    this.service.listWarehouses().subscribe({
      next: w => {
        this.warehouses.set(w.filter(x => x.isActive));
        this.warehouseId = w[0]?.id ?? '';
        this.reload();
      },
      error: () => this.loading.set(false)
    });
  }

  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} - ${w.name}`, value: w.id }));

  scan(): void {
    if (!this.scanCode.trim()) return;
    this.service.scanInventoryCode(this.scanCode.trim(), this.warehouseId || null).subscribe({
      next: row => this.lookup.set(row),
      error: () => this.lookup.set(null)
    });
  }

  openTask(id: string): void {
    void this.router.navigate(['/inventory/warehouse-tasks'], { queryParams: { taskId: id } });
  }

  filterTasksByBin(binId: string): void {
    this.filteredTasks.set(this.tasks().filter(t => t.fromBinId === binId || t.toBinId === binId));
  }

  completePutAway(task: WarehouseTask): void {
    this.service.completePutAwayTask(task.id, `op-put-${task.id}`).subscribe({ next: () => this.reload() });
  }

  completePick(task: WarehouseTask): void {
    this.service.completePickTask(task.id, `op-pick-${task.id}`).subscribe({ next: () => this.reload() });
  }

  completePack(task: WarehouseTask): void {
    this.service.completePackTask(task.id).subscribe({ next: () => this.reload() });
  }

  completeShip(task: WarehouseTask): void {
    this.service.completeShipTask(task.id).subscribe({ next: () => this.reload() });
  }

  createShip(task: WarehouseTask): void {
    this.service.createShipTaskFromPack(task.id).subscribe({ next: () => this.reload() });
  }

  reload(): void {
    this.service.listWarehouseTasks('Pending').subscribe({
      next: pending => {
        this.service.listWarehouseTasks('Completed', 'Pack').subscribe({
          next: completedPack => {
            const all = [...pending, ...completedPack.filter(t => t.taskType === 'Pack')];
            this.tasks.set(all);
            this.filteredTasks.set(all);
            this.loading.set(false);
          },
          error: () => this.loading.set(false)
        });
      },
      error: () => this.loading.set(false)
    });
  }
}
